import {
  Box,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  AccountTransaction,
  Big,
  PaymentMethodType,
  StoreSnapshotsResult,
  TransactionCategory,
  TransactionFlow,
  bigSum,
  formatCurrency,
} from '@bofrak-backend/shared';
import { Spacer, Skeleton } from '@chakra-ui/react';
import { formatDate } from '@bofrak-backend/shared-i18n';
import { useMemo, useState } from 'react';
import { useQueries } from 'react-query';
import { useRecoilValue } from 'recoil';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { apiAdapter } from '../../api/backend';
import { merchantAtom, storeAtom } from '../../recoil/atoms';
import { useSharedUiTranslation, type SharedUiT } from '../../../i18n';

interface MoneyValueDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  dateRange: { start: Date | null; end: Date | null };
  accountTransactions?: AccountTransaction[];
  moneyStart?: number;
  moneyEnd?: number;
}

/* ── Helpers ── */

export interface MoneyTimePoint {
  time: Date;
  total: number;
  byMethod: Record<string, number>;
}

const getMethodLabels = (t: SharedUiT): Record<string, string> => ({
  [PaymentMethodType.CASH]: t('Cash'),
  [PaymentMethodType.CARD]: t('Card'),
  [PaymentMethodType.MOBILE_MONEY]: t('Mobile Money'),
  [PaymentMethodType.BANK_TRANSFER]: t('Bank Transfer'),
  [PaymentMethodType.CHEQUE]: t('Cheque'),
  [PaymentMethodType.COUPON]: t('Coupon'),
  [PaymentMethodType.LOYALTY_POINTS]: t('Loyalty Pts'),
  [PaymentMethodType.DEBT]: t('Debt'),
  [PaymentMethodType.OTHER]: t('Other'),
});

const METHOD_COLORS: Record<string, string> = {
  [PaymentMethodType.CASH]: '#38A169',
  [PaymentMethodType.CARD]: '#3182CE',
  [PaymentMethodType.MOBILE_MONEY]: '#DD6B20',
  [PaymentMethodType.BANK_TRANSFER]: '#805AD5',
  [PaymentMethodType.CHEQUE]: '#718096',
  [PaymentMethodType.COUPON]: '#D53F8C',
  [PaymentMethodType.LOYALTY_POINTS]: '#D69E2E',
  [PaymentMethodType.DEBT]: '#E53E3E',
  [PaymentMethodType.OTHER]: '#4A5568',
};

export function computeSamplePoints(start: Date, end: Date): Date[] {
  const durationMs = end.getTime() - start.getTime();
  const maxPoints = 20;
  const minIntervalMs = 15 * 60 * 1000;
  const intervalMs = Math.max(durationMs / maxPoints, minIntervalMs);

  const points: Date[] = [start];
  let current = start.getTime() + intervalMs;
  while (current < end.getTime() - intervalMs * 0.5) {
    points.push(new Date(current));
    current += intervalMs;
  }
  points.push(end);
  return points;
}

export function aggregateSnapshot(
  result: StoreSnapshotsResult,
): MoneyTimePoint {
  const byMethod: Record<string, number> = {};
  let total = 0;
  for (const userSnap of result.snapshots) {
    for (const snap of userSnap.account_snapshots) {
      const key = snap.payment_type;
      byMethod[key] = new Big(byMethod[key] || 0).plus(snap.amount).toNumber();
      total = new Big(total).plus(snap.amount).toNumber();
    }
  }
  return { time: new Date(result.at_time), total, byMethod };
}

/* ── History Tab ── */

function MoneyHistoryTab({ points }: { points: MoneyTimePoint[] }) {
  const { t } = useSharedUiTranslation();
  const methodLabels = getMethodLabels(t);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const descending = useMemo(() => [...points].reverse(), [points]);

  if (descending.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
        {t('No money snapshots in this date range.')}
      </Text>
    );
  }

  return (
    <VStack spacing={0} align="stretch">
      <HStack
        px={3}
        py={2}
        bg="gray.50"
        borderBottom="1px solid"
        borderColor="gray.200">
        <Text flex={1} fontSize="2xs" fontWeight="bold" color="gray.500">
          {t('Date')}
        </Text>
        <Text
          flex={1}
          fontSize="2xs"
          fontWeight="bold"
          color="gray.500"
          textAlign="right">
          {t('Total Money')}
        </Text>
      </HStack>

      {descending.map((point, i) => {
        const realIdx = points.length - 1 - i;
        const isExpanded = expandedIdx === realIdx;
        const methods = Object.entries(point.byMethod).filter(
          ([, v]) => v !== 0,
        );

        return (
          <Box key={realIdx}>
            <HStack
              px={3}
              py={2}
              borderBottom="1px solid"
              borderColor="gray.50"
              cursor="pointer"
              _hover={{ bg: 'blue.50' }}
              onClick={() => setExpandedIdx(isExpanded ? null : realIdx)}>
              <Text
                flex={1}
                fontSize="2xs"
                color="gray.700"
                whiteSpace="nowrap">
                {formatDate(point.time, 'dd.MM.yy HH:mm')}
              </Text>
              <Text
                flex={1}
                fontSize="2xs"
                fontWeight="semibold"
                color="gray.800"
                textAlign="right"
                whiteSpace="nowrap">
                {formatCurrency(point.total, true)}
              </Text>
            </HStack>

            <Collapse in={isExpanded} animateOpacity>
              <VStack
                spacing={0}
                align="stretch"
                bg="gray.50"
                pl={6}
                pr={3}
                py={1}>
                {methods.map(([method, amount]) => (
                  <HStack key={method} py={1}>
                    <Box
                      w="8px"
                      h="8px"
                      borderRadius="full"
                      bg={METHOD_COLORS[method] || 'gray.400'}
                      flexShrink={0}
                    />
                    <Text flex={1} fontSize="2xs" color="gray.600">
                      {methodLabels[method] || method}
                    </Text>
                    <Text
                      fontSize="2xs"
                      fontWeight="medium"
                      color="gray.700"
                      textAlign="right">
                      {formatCurrency(amount, true)}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </Collapse>
          </Box>
        );
      })}
    </VStack>
  );
}

/* ── Graph Tab ── */

function MoneyGraphTab({ points }: { points: MoneyTimePoint[] }) {
  const { t } = useSharedUiTranslation();
  const methodLabels = getMethodLabels(t);
  const allMethods = useMemo(() => {
    const set = new Set<string>();
    for (const p of points) {
      for (const key of Object.keys(p.byMethod)) {
        if (p.byMethod[key] !== 0) set.add(key);
      }
    }
    return Array.from(set);
  }, [points]);

  const chartData = useMemo(
    () =>
      points.map((p) => ({
        name: formatDate(p.time, 'dd.MM HH:mm'),
        ...Object.fromEntries(allMethods.map((m) => [m, p.byMethod[m] || 0])),
      })),
    [points, allMethods],
  );

  if (chartData.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
        {t('No data to display.')}
      </Text>
    );
  }

  return (
    <Box height="300px" w="100%">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 9 }}
            interval="preserveStartEnd"
            angle={-35}
            textAnchor="end"
            height={50}
          />
          <YAxis
            tick={{ fontSize: 9 }}
            tickFormatter={(v: number) => formatCurrency(v, true)}
            width={65}
          />
          <Tooltip
            formatter={(v: number, name: string) => [
              formatCurrency(v, true),
              methodLabels[name] || name,
            ]}
            labelStyle={{ fontSize: 11 }}
            contentStyle={{ fontSize: 11 }}
          />
          <Legend
            formatter={(value: string) => (
              <Text as="span" fontSize="2xs">
                {methodLabels[value] || value}
              </Text>
            )}
          />
          {allMethods.map((method) => (
            <Bar
              key={method}
              dataKey={method}
              stackId="money"
              fill={METHOD_COLORS[method] || '#A0AEC0'}
              name={method}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

/* ── Category Summary ── */

const getCategoryConfig = (
  t: SharedUiT,
): {
  category: TransactionCategory;
  label: string;
  color: string;
}[] => [
  {
    category: TransactionCategory.SALES_RECEIPT,
    label: t('Sales'),
    color: '#38A169',
  },
  {
    category: TransactionCategory.SALES_REFUND,
    label: t('Refunds'),
    color: '#E53E3E',
  },
  {
    category: TransactionCategory.DEBT_PAYMENT,
    label: t('Debt Payments'),
    color: '#3182CE',
  },
  {
    category: TransactionCategory.LOAN_PAYMENT,
    label: t('Loan Payments'),
    color: '#319795',
  },
  {
    category: TransactionCategory.LOAN_GIVEN,
    label: t('Loans Given'),
    color: '#DD6B20',
  },
  {
    category: TransactionCategory.TRANSFER_IN,
    label: t('Transfers In'),
    color: '#805AD5',
  },
  {
    category: TransactionCategory.TRANSFER_OUT,
    label: t('Transfers Out'),
    color: '#D69E2E',
  },
  {
    category: TransactionCategory.INVESTMENT,
    label: t('Investments'),
    color: '#2B6CB0',
  },
  {
    category: TransactionCategory.DIVIDEND,
    label: t('Dividends'),
    color: '#B7791F',
  },
  {
    category: TransactionCategory.SUPPLIER_PAYMENT,
    label: t('Supplier Payments'),
    color: '#C53030',
  },
  {
    category: TransactionCategory.SUPPLIER_REFUND,
    label: t('Supplier Refunds'),
    color: '#2C7A7B',
  },
  {
    category: TransactionCategory.EXPENSE,
    label: t('Expenses'),
    color: '#718096',
  },
  {
    category: TransactionCategory.CORRECTION,
    label: t('Corrections'),
    color: '#4A5568',
  },
  {
    category: TransactionCategory.OPENING_CORRECTION,
    label: t('Opening Corrections'),
    color: '#553C9A',
  },
  {
    category: TransactionCategory.CLOSING_CORRECTION,
    label: t('Closing Corrections'),
    color: '#975A16',
  },
];

function useCategoryTotals(transactions: AccountTransaction[]) {
  return useMemo(() => {
    const totals = new Map<TransactionCategory, Big>();
    for (const tx of transactions) {
      const sign = tx.flow === TransactionFlow.INFLOW ? 1 : -1;
      const current = totals.get(tx.category) ?? new Big(0);
      totals.set(
        tx.category,
        current.plus(new Big(Math.abs(tx.amount)).times(sign)),
      );
    }
    const result = new Map<TransactionCategory, number>();
    for (const [cat, val] of totals) {
      result.set(cat, val.toNumber());
    }
    return result;
  }, [transactions]);
}

function MoneySummarySection({
  moneyStart = 0,
  moneyEnd = 0,
  accountTransactions = [],
  isLoading,
}: {
  moneyStart?: number;
  moneyEnd?: number;
  accountTransactions?: AccountTransaction[];
  isLoading?: boolean;
}) {
  const { t } = useSharedUiTranslation();
  const categoryTotals = useCategoryTotals(accountTransactions);

  const activeCategories = getCategoryConfig(t).filter(
    (cfg) => (categoryTotals.get(cfg.category) ?? 0) !== 0,
  );

  const endValueCalc = bigSum(
    activeCategories.map((cfg) => categoryTotals.get(cfg.category) ?? 0),
  )
    .plus(moneyStart)
    .toNumber();

  const discrepancy = new Big(endValueCalc).minus(moneyEnd).toNumber();
  const discrepancyPercent =
    moneyEnd !== 0
      ? new Big(discrepancy).div(moneyEnd).times(100).toNumber()
      : 0;

  return (
    <VStack spacing={1} align="stretch" mb={3}>
      {/* Start of period */}
      <HStack bg="gray.50" borderRadius="md" px={3} py={1}>
        <Text fontSize="sm" color="gray.600" mb={0.5}>
          {t('Start of Period')}
        </Text>
        <Spacer />
        <Skeleton isLoaded={!isLoading}>
          <Text fontSize="sm" fontWeight="bold" color="gray.800">
            {formatCurrency(moneyStart, true)}
          </Text>
        </Skeleton>
      </HStack>

      {/* Per-category breakdown */}
      {isLoading ? (
        <VStack>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} height="28px" borderRadius="md" />
          ))}
        </VStack>
      ) : (
        activeCategories.length > 0 &&
        activeCategories.map((cfg) => {
          const val = categoryTotals.get(cfg.category) ?? 0;
          return (
            <HStack
              key={cfg.category}
              bg="gray.50"
              width="full"
              borderRadius="md"
              px={2}
              py={1}>
              <Text fontSize="xs" color="gray.600">
                {cfg.label}
              </Text>
              <Spacer />
              <Text
                fontSize="xs"
                fontWeight="bold"
                color={val >= 0 ? 'green.600' : 'red.500'}>
                {val >= 0 ? '+' : ''}
                {formatCurrency(val, true)}
              </Text>
            </HStack>
          );
        })
      )}

      {/* End of period (calculated) */}
      <HStack bg="gray.50" borderRadius="md" px={3} py={1}>
        <Text fontSize="sm" color="gray.600" mb={0.5}>
          {t('End of Period (Calculated)')}
        </Text>
        <Spacer />
        <Skeleton isLoaded={!isLoading}>
          <Text fontSize="sm" fontWeight="bold" color="gray.800">
            {formatCurrency(endValueCalc, true)}
          </Text>
        </Skeleton>
      </HStack>

      {/* End of period (actual) */}
      <HStack bg="gray.50" borderRadius="md" px={3} py={1}>
        <Text fontSize="sm" color="gray.600" mb={0.5}>
          {t('End of Period (Actual)')}
        </Text>
        <Spacer />
        <Skeleton isLoaded={!isLoading}>
          <Text fontSize="sm" fontWeight="bold" color="gray.800">
            {formatCurrency(moneyEnd, true)}
          </Text>
        </Skeleton>
      </HStack>

      {/* Discrepancy */}
      <HStack
        bg="gray.50"
        borderRadius="md"
        px={3}
        py={1}
        color={discrepancy !== 0 ? 'red.600' : 'green.600'}>
        <Text fontSize="xs" mb={0.5}>
          {t('Discrepancy ({{percent}}%)', {
            percent: `${discrepancyPercent >= 0 ? '+' : ''}${discrepancyPercent.toFixed(1)}`,
          })}
        </Text>
        <Spacer />
        <Text fontSize="xs" fontWeight="bold">
          {discrepancy >= 0 ? '+' : ''}
          {formatCurrency(discrepancy, true)}
        </Text>
      </HStack>
    </VStack>
  );
}

/* ── Main Drawer ── */

export function MoneyValueDrawer({
  isOpen,
  onClose,
  dateRange,
  accountTransactions = [],
  moneyStart = 0,
  moneyEnd = 0,
}: MoneyValueDrawerProps) {
  const { t } = useSharedUiTranslation();
  const merchant = useRecoilValue(merchantAtom);
  const store = useRecoilValue(storeAtom);

  const samplePoints = useMemo(() => {
    if (!dateRange.start || !dateRange.end) return [];
    return computeSamplePoints(dateRange.start, dateRange.end);
  }, [dateRange.start, dateRange.end]);

  const snapshotQueries = useQueries(
    samplePoints.map((point) => ({
      queryKey: [
        'storeSnapshotsTimeline',
        merchant?.id,
        store?.id,
        point.toISOString(),
      ],
      queryFn: () =>
        apiAdapter.getStoreSnapshotsAtTime({
          merchant_id: merchant!.id,
          store_id: store!.id,
          at_time: point.toISOString(),
        }),
      enabled: isOpen && !!merchant?.id && !!store?.id,
      staleTime: 5 * 60 * 1000,
    })),
  );

  const isLoading = snapshotQueries.some((q) => q.isLoading);
  const loadedCount = snapshotQueries.filter((q) => q.isSuccess).length;

  const timePoints: MoneyTimePoint[] = useMemo(() => {
    const results: MoneyTimePoint[] = [];
    for (let i = 0; i < snapshotQueries.length; i++) {
      const q = snapshotQueries[i];
      if (q.data) {
        results.push(aggregateSnapshot(q.data));
      }
    }
    return results;
  }, [snapshotQueries]);

  const subtitle =
    dateRange.start && dateRange.end
      ? `${formatDate(dateRange.start, 'dd MMM yyyy HH:mm')} – ${formatDate(dateRange.end, 'dd MMM yyyy HH:mm')}`
      : '';

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader pb={1} px={3}>
          <Text fontSize="md" fontWeight="bold">
            {t('Available Money')}
          </Text>
          {subtitle && (
            <Text fontSize="xs" color="gray.500">
              {subtitle}
            </Text>
          )}
        </DrawerHeader>

        <DrawerBody px={3} pb={6}>
          <MoneySummarySection
            moneyStart={moneyStart}
            moneyEnd={moneyEnd}
            accountTransactions={accountTransactions}
            isLoading={isLoading}
          />

          {isLoading && (
            <HStack justify="center" py={3} spacing={2}>
              <Spinner size="sm" color="blue.500" />
              <Text fontSize="xs" color="gray.500">
                {t('Loading {{loaded}}/{{total}} snapshots…', {
                  loaded: loadedCount,
                  total: samplePoints.length,
                })}
              </Text>
            </HStack>
          )}

          <Tabs isFitted size="sm" variant="line" colorScheme="blue">
            <TabList>
              <Tab fontSize="xs">{t('History')}</Tab>
              <Tab fontSize="xs">{t('Graph')}</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0} py={2}>
                <MoneyHistoryTab points={timePoints} />
              </TabPanel>
              <TabPanel px={0} py={2}>
                <MoneyGraphTab points={timePoints} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
